import { z } from "zod";
import { defineStepConfig } from "../../../registry/helpers";
import { VisibilityPredicateType } from "../../../registry/visibility";
import { IntegrationKey } from "@erp/shared-types";

/**
 * Ingest AR Payments from Famous Step
 *
 * Runs only the payment-related phases of the AR ingestion pipeline:
 *   5a. ingestArBankTransactions   — BankTransaction rows from BANK_ACTIVITY
 *   5b. ingestArPayments           — Payment rows, reconciled against remittances
 *   6.  ingestArPaymentApplications — Payment-to-invoice links
 *
 * Useful for syncing new cash receipts without re-ingesting the full invoice
 * tree (Phases 0–4). Requires that Customers and Invoices already exist in
 * Postgres from a prior full AR sync.
 */
export const ingestArPaymentsStep = defineStepConfig({
  type: "ingest_ar_payments",
  name: "Ingest AR Payments from Famous",
  description:
    "Sync bank transactions, payments, and payment applications from Famous without re-ingesting invoices",
  category: "Integration",
  libraryModule: "Famous",
  visibility: [
    [
      {
        type: VisibilityPredicateType.Integration,
        integration: IntegrationKey.Famous,
      },
    ],
  ],
  inputSchema: z.object({}),

  outputSchema: z.object({
    bankTransactions: z.object({
      success: z.number(),
      failed: z.number(),
      skipped: z.number(),
    }),
    payments: z.object({
      success: z.number(),
      failed: z.number(),
      skipped: z.number(),
    }),
    paymentApplications: z.object({
      success: z.number(),
      failed: z.number(),
      skipped: z.number(),
    }),
    durationMs: z.number(),
  }),

  configSchema: z.object({
    sinceDays: z
      .number()
      .int()
      .positive()
      .nullable()
      .default(null)
      .describe(
        "Lookback window in days for ba.ACTIVITYDATE. " +
          "Null = no date restriction (full sync of all bank activity).",
      ),
    activityDateAfter: z
      .string()
      .regex(/^\d{4}-\d{2}-\d{2}$/, "Date must be in YYYY-MM-DD format")
      .nullable()
      .default(null)
      .describe(
        "Only ingest bank activity on or after this date (YYYY-MM-DD).",
      ),
    activityDateBefore: z
      .string()
      .regex(/^\d{4}-\d{2}-\d{2}$/, "Date must be in YYYY-MM-DD format")
      .nullable()
      .default(null)
      .describe(
        "Only ingest bank activity on or before this date (YYYY-MM-DD).",
      ),
  }),

  execution: {
    startToCloseTimeout: "60 minutes",
  },

  emits: [] as const,
});
